import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, useRouterState } from '@tanstack/react-router'
import { CreateMetadataModeFormValue } from 'bank-contacts/src/types/forms'
import { useFormData } from 'brief-form'
import { routes } from 'commons/constants/routes'
import { FilterCompanyContactsWebPage } from 'commons/service/bank/companyDetails'
import { CompanyContactsWebPage, Contact } from 'commons/types/DTO/bank/companyDetails/contact'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

import { useDisconnectContactOnWebpage } from './useDisconnectContactOnWebPage'
import { useGetCompanyContactsWebPage } from './useGetCompanyContactsWebPage'

export const useComponentsEssence = (
  companyId: number,
  initialContacts: Contact[],
  initialContactsTotalCount: number
) => {
  const navigate = getRouteApi('/_app/relationships/$id').useNavigate()
  const { pathname } = useRouterState({ select: (s) => s.location })
  const queryClient = useQueryClient()

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10,
      contactsType: 'all'
    }),
    [companyId]
  )

  const [webPageData, setWebPageData] = useState<CompanyContactsWebPage>({
    contacts: initialContacts,
    contactsTotalCount: initialContactsTotalCount
  })

  const [tableFilter, setTableFilter] = useState<FilterCompanyContactsWebPage>(defaultFilter)

  useEffect(() => {
    setWebPageData({
      contacts: initialContacts,
      contactsTotalCount: initialContactsTotalCount
    })
  }, [initialContacts, initialContactsTotalCount])

  const { isFetched, isFetching, data, invalidate } = useGetCompanyContactsWebPage(tableFilter)

  useEffect(() => {
    if (isFetched && data) {
      setWebPageData(data)
    }
  }, [isFetched, data])

  const onEditContactMetaData = useCallback(
    (c: Contact) =>
      // TODO: look url in runtime
      navigate({
        to: routes.bank.relationships.contact.edit,
        params: { contactId: c.id.toString() },
        resetScroll: false,
        hashScrollIntoView: false,
        startTransition: false,
        replace: true
      }),
    [navigate, companyId]
  )

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      tableState.onPageChange(page, size)

      const newFilter = {
        ...tableFilter,
        page,
        size
      }

      setTableFilter(newFilter)
    },
    [setTableFilter, tableFilter, tableState]
  )

  const onFilterChange = async (value: FilterCompanyContactsWebPage) => {
    tableState.filterForm.set({ value })
    tableState.onPageChange(defaultFilter.page, tableState.size)

    const newFilter = {
      ...value,
      page: defaultFilter.page
    }

    setTableFilter(newFilter)
  }

  const filterForm = useFormData<FilterCompanyContactsWebPage>({
    initialValue: {
      relationshipId: companyId,
      page: defaultFilter.page,
      size: defaultFilter.size,
      contactsType: 'all'
    }
  })

  const originalChangeHandler = filterForm.config.onChange
  filterForm.config.onChange = useCallback(
    (v, e) => {
      onFilterChange(v)
      originalChangeHandler(v, e)
    },
    [originalChangeHandler, tableState]
  )

  const clearFilter = useCallback(() => {
    onFilterChange(defaultFilter)
    filterForm.set({ reset: true })
  }, [onFilterChange, filterForm])

  const getCompanyContactsAfterDeleteWebPage = () => {
    const isDeletingContactLastOnPage = tableFilter.size * tableFilter.page >= webPageData.contactsTotalCount

    const page = isDeletingContactLastOnPage
      ? Math.ceil((webPageData.contactsTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    tableState.onPageChange(page, tableState.size)

    const filter = { ...tableFilter, page }

    // if contact isn't last on the table page we just reload page
    // else change filter and it also pulls new request
    if (filter.page === tableFilter.page) invalidate()
    else setTableFilter(filter)
  }

  const [contactForDelete, setContactForDelete] = useState<Contact | undefined>(undefined)
  const { routine: disconnectMutation } = useDisconnectContactOnWebpage({
    onSuccess: async (payload) => {
      getCompanyContactsAfterDeleteWebPage()
      queryClient.invalidateQueries({ queryKey: ['companyDetails', payload.companyId] })
    },
    onError: (error) => handlerServerError(error)
  })

  const disconnectContact = useCallback(async () => {
    try {
      await disconnectMutation({
        contactId: contactForDelete?.id || 0,
        companyId: companyId
      })

      Toast.successSmall(`Contact and company were detached.`)
    } catch (error) {
      handlerServerError(error)
    }
  }, [disconnectMutation, contactForDelete])

  const { open: openDeleteConfirmation, Confirmation: DeleteConfirmation } = useConfirmation({
    message: `Are you sure you want to disconnect ${contactForDelete?.name} contact from this company?`,
    onConfirm: disconnectContact,
    confirmationButtonText: 'Disconnect',
    isConfirmationNegative: true
  })

  const onDeleteContact = (contact: Contact | undefined) => {
    setContactForDelete(contact)
    openDeleteConfirmation()
  }

  const [openedAddModeDialog, setOpenedAddModelDialog] = useState(false)

  const onSubmitChooseAddModeDialog = useCallback(
    (data: CreateMetadataModeFormValue) => {
      setOpenedAddModelDialog(false)

      if (data.mode === 'new') {
        navigate({
          to: `/contacts/new`,
          // this returnUrl's used for successful creating contact
          search: {
            successReturnUrl: pathname + '/contact/add/',
            cancelReturnUrl: pathname + '#contacts'
          },
          replace: true
        })
      } else {
        // TODO: look url on runtime
        navigate({
          to: routes.bank.relationships.contact.add,
          params: { contactId: data.contact_id!.toString() },
          resetScroll: false,
          hashScrollIntoView: false,
          startTransition: false,
          replace: true
        })
      }
    },
    [navigate, pathname, companyId]
  )

  return {
    webPageData,
    isFetching,
    tableState,
    filterForm,
    changePagination,
    onDeleteContact,
    DeleteConfirmation,
    openDeleteConfirmation,
    disconnectContact,
    openedAddModeDialog,
    setOpenedAddModelDialog,
    onSubmitChooseAddModeDialog,
    clearFilter,
    onEditContactMetaData
  }
}
